/* NV Joailliers — theme.js
   ──────────────────────────────────────────────────────────
   Handles:
   - PDP variant selection + price/url update + add-to-cart
   - PDP configurator (gallery thumbs, width slider, finish, engraving, live preview)
   - PDP tabs
   - Cart line item qty +/- buttons
   - Cart count refresh after AJAX add (if used)
*/
(function () {
  'use strict';

  // ── Utilities ──────────────────────────────────────────────────────────
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  function formatMoney(cents) {
    // Try Shopify's moneyFormat if available, else fallback to €
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeMoneyFormat || '{{amount}} €');
    }
    const v = (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return v + ' €';
  }

  // ── Product Detail Page ────────────────────────────────────────────────
  function initPDP() {
    const section = $('[data-product-section]');
    if (!section) return;

    const variantsJson = $('[data-product-json]', section);
    const optionsJson = $('[data-product-options-json]', section);
    if (!variantsJson || !optionsJson) return;

    let variants, options;
    try {
      variants = JSON.parse(variantsJson.textContent);
      options = JSON.parse(optionsJson.textContent);
    } catch (e) {
      console.warn('NV: failed to parse product JSON', e);
      return;
    }

    // Track selected options
    const selected = options.map((_, i) => {
      const initial = variants.find(v => v.available) || variants[0];
      return initial ? initial['option' + (i + 1)] : null;
    });

    function findVariant() {
      return variants.find(v =>
        options.every((_, i) => v['option' + (i + 1)] === selected[i])
      );
    }

    function updateAvailability() {
      // For each option button, mark as sold-out if no available variant matches given (other options fixed)
      $$('[data-variant-option]', section).forEach(btn => {
        const idx = parseInt(btn.getAttribute('data-option-index'), 10);
        const val = btn.getAttribute('data-option-value');
        const trial = selected.slice();
        trial[idx] = val;
        const matching = variants.find(v =>
          options.every((_, i) => v['option' + (i + 1)] === trial[i])
        );
        if (!matching || !matching.available) {
          btn.classList.add('nv-opt--soldout');
        } else {
          btn.classList.remove('nv-opt--soldout');
        }
      });
    }

    function updatePrice() {
      const v = findVariant();
      const priceEl = $('[data-pdp-price]', section);
      const priceSubEl = $('[data-pdp-price-sub]', section);
      const ctaPriceEl = $('[data-cta-price]', section);
      const ctaBtn = $('[data-add-to-cart]', section);
      const variantInput = $('[data-variant-id]', section);
      const ctaLabel = $('[data-cta-label]', section);

      if (!v) {
        if (ctaBtn) { ctaBtn.disabled = true; }
        if (ctaLabel) { ctaLabel.textContent = 'Combinaison indisponible'; }
        return;
      }

      if (variantInput) variantInput.value = v.id;
      if (priceEl) priceEl.textContent = formatMoney(v.price);
      if (priceSubEl) priceSubEl.textContent = 'ou 4× ' + formatMoney(Math.round(v.price / 4)) + ' sans frais';
      if (ctaBtn) ctaBtn.disabled = !v.available;
      if (ctaPriceEl) ctaPriceEl.textContent = formatMoney(v.price);
      if (ctaLabel && !v.available) ctaLabel.textContent = 'Indisponible';
      else if (ctaLabel && v.available) ctaLabel.innerHTML = 'Ajouter · <span data-cta-price>' + formatMoney(v.price) + '</span>';

      // Update URL with variant param (without reload)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(null, '', url.toString());
      } catch (e) {}
    }

    // Variant option buttons
    $$('[data-variant-option]', section).forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-option-index'), 10);
        const val = btn.getAttribute('data-option-value');
        selected[idx] = val;
        // Update sibling buttons in same option group
        $$('[data-variant-option][data-option-index="' + idx + '"]', section).forEach(b => {
          const isMe = b === btn;
          b.classList.toggle('is-active', isMe);
          b.setAttribute('aria-checked', isMe ? 'true' : 'false');
        });
        updatePrice();
        updateAvailability();
      });
    });

    // Configurator: width slider
    const widthSlider = $('[data-width-slider]', section);
    const widthDisplay = $('[data-width-display]', section);
    const widthInput = $('[data-width-input]', section);
    const detailWidth = $('[data-detail-width]', section);

    function updateWidth(val) {
      const v = parseFloat(val);
      const str = v.toFixed(1).replace('.', ',') + ' mm';
      if (widthDisplay) widthDisplay.textContent = str;
      if (widthInput) widthInput.value = str;
      if (detailWidth) detailWidth.textContent = str;
      updateLiveRing();
    }
    if (widthSlider) {
      widthSlider.addEventListener('input', e => updateWidth(e.target.value));
      // Initialize hidden value
      updateWidth(widthSlider.value);
    }

    // Configurator: finish (property)
    const finishInput = $('[data-finish-input]', section);
    const detailFinish = $('[data-detail-finish]', section);
    $$('[data-finish]', section).forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.getAttribute('data-finish-value');
        $$('[data-finish]', section).forEach(b => {
          const isMe = b === btn;
          b.classList.toggle('is-active', isMe);
          b.setAttribute('aria-checked', isMe ? 'true' : 'false');
        });
        if (finishInput) finishInput.value = val;
        if (detailFinish) detailFinish.textContent = val;
        updateLiveRing();
      });
    });

    // Configurator: engraving counter
    const engraving = $('[data-engraving]', section);
    const engCount = $('[data-engraving-count]', section);
    if (engraving && engCount) {
      engraving.addEventListener('input', () => {
        engCount.textContent = engraving.value.length;
        updateLiveRing();
      });
    }

    // Gallery thumbs (and "live" thumb)
    const mainImage = $('[data-main-image]', section);
    const liveRing = $('[data-live-ring]', mainImage);
    const liveCore = $('[data-live-core]', liveRing);
    const liveStone = $('[data-live-stone]', liveRing);
    const liveEngraving = $('[data-live-engraving]', liveRing);

    function getMetalSwatch() {
      const activeMetal = $('[data-variant-option].is-active[data-option-index]', section);
      if (!activeMetal) return '#d4b26a';
      const val = (activeMetal.getAttribute('data-option-value') || '').toLowerCase();
      if (val.includes('rose')) return '#d4a89a';
      if (val.includes('blanc')) return '#d8d4c8';
      return '#d4b26a';
    }

    function updateLiveRing() {
      if (!liveRing || !liveCore) return;
      const w = widthSlider ? parseFloat(widthSlider.value) : 2.8;
      const swatch = getMetalSwatch();
      const finish = finishInput ? finishInput.value.toLowerCase() : 'poli';
      liveCore.style.border = (w * 5) + 'px solid ' + swatch;
      if (finish.includes('martel')) {
        liveCore.style.background = 'repeating-radial-gradient(circle at 30% 30%, ' + swatch + ', ' + swatch + ' 3px, rgba(0,0,0,0.15) 3px, rgba(0,0,0,0.15) 4px)';
        liveCore.style.filter = '';
      } else if (finish.includes('sabl')) {
        liveCore.style.background = '';
        liveCore.style.filter = 'contrast(0.9) brightness(0.95)';
      } else {
        liveCore.style.background = '';
        liveCore.style.filter = '';
      }
      if (liveEngraving) {
        const v = engraving ? engraving.value : '';
        liveEngraving.textContent = v ? '« ' + v + ' »' : '';
      }
      if (liveStone) liveStone.hidden = true; // no stone in basic configurator
    }

    function showLive() {
      $$('[data-thumb-index]', section).forEach(t => t.classList.remove('is-active'));
      const liveThumb = $('[data-thumb-live]', section);
      if (liveThumb) liveThumb.classList.add('is-active');
      // hide image, show live
      const imgWrap = $('.nv-image-wrap, .nv-placeholder', mainImage);
      if (imgWrap && imgWrap !== liveRing) imgWrap.style.display = 'none';
      if (liveRing) {
        liveRing.hidden = false;
        liveRing.style.display = '';
      }
      updateLiveRing();
    }
    function showThumb(index) {
      $$('[data-thumb-index]', section).forEach((t, i) => t.classList.toggle('is-active', i === index));
      const liveThumb = $('[data-thumb-live]', section);
      if (liveThumb) liveThumb.classList.remove('is-active');
      if (liveRing) liveRing.hidden = true;
      // Show the appropriate placeholder/img for the index
      // In this basic theme we don't swap real images by index; only the live preview is dynamic.
      // The user could enhance by mapping thumbs to specific product.images.
      const imgWrap = mainImage.querySelector('.nv-image-wrap, .nv-placeholder');
      if (imgWrap) imgWrap.style.display = '';
    }

    $$('[data-thumb-index]', section).forEach((btn, i) => {
      btn.addEventListener('click', () => showThumb(i));
    });
    const liveThumb = $('[data-thumb-live]', section);
    if (liveThumb) liveThumb.addEventListener('click', showLive);

    // Tabs
    const tabsContainer = $('[data-tabs]', section);
    if (tabsContainer) {
      $$('[data-tab]', tabsContainer).forEach(tab => {
        tab.addEventListener('click', () => {
          const id = tab.getAttribute('data-tab');
          $$('[data-tab]', tabsContainer).forEach(t => t.classList.toggle('is-active', t === tab));
          $$('[data-tab-panel]', tabsContainer).forEach(p => p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === id));
        });
      });
    }

    // Init
    updateAvailability();
    updatePrice();
  }

  // ── Cart page qty controls ─────────────────────────────────────────────
  function initCart() {
    const form = $('[data-cart-form]');
    if (!form) return;
    $$('[data-cart-line]', form).forEach(line => {
      const input = $('[data-qty-input]', line);
      const minus = $('[data-qty-minus]', line);
      const plus = $('[data-qty-plus]', line);
      if (!input) return;
      function bump(delta) {
        const v = Math.max(0, parseInt(input.value, 10) + delta);
        input.value = v;
        form.submit();
      }
      if (minus) minus.addEventListener('click', () => bump(-1));
      if (plus) plus.addEventListener('click', () => bump(1));
    });
  }

  // ── Boot ───────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initPDP(); initCart(); });
  } else {
    initPDP();
    initCart();
  }

  // Hook into Shopify theme editor for live previews
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => { initPDP(); initCart(); });
  }
})();
